import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { FadeUp, Stagger, StaggerItem } from "./Reveal";
import { GALLERY } from "../data/site";

export default function StudioGallery({
    items = GALLERY,
    showHeading = true,
    showLink = false,
    stagger = true,
}) {
    const [active, setActive] = useState(null);
    const isOpen = active !== null;

    const close = useCallback(() => setActive(null), []);
    const go = useCallback(
        (dir) =>
            setActive((cur) =>
                cur === null ? cur : (cur + dir + items.length) % items.length
            ),
        [items.length]
    );

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") go(1);
            if (e.key === "ArrowLeft") go(-1);
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [isOpen, close, go]);

    return (
        <section
            data-testid="studio-gallery-section"
            className="bg-ice py-20 sm:py-28 md:py-32"
        >
            {showHeading && (
                <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-8 px-4 sm:px-8 md:px-12">
                    <SectionHeading
                        overline="Lo Studio"
                        title="Un ambiente semplice, curato, familiare."
                        lead="Uno studio luminoso e tranquillo, pensato per farti sentire a tuo agio dalla sala d'attesa alla poltrona."
                        testid="studio-gallery-heading"
                    />
                    {showLink && (
                        <FadeUp delay={0.2}>
                            <Link
                                to="/studio"
                                data-testid="studio-gallery-all-link"
                                className="group inline-flex items-center gap-2 text-sm font-normal text-petrol transition-colors hover:text-petrol-dark"
                            >
                                Tutte le foto dello studio
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </FadeUp>
                    )}
                </div>
            )}
            <div className="mx-auto max-w-[96rem] px-4 sm:px-8">
                <Stagger
                    testid="studio-gallery-grid"
                    className={`grid grid-cols-1 gap-5 lg:grid-cols-12 ${showHeading ? "mt-16" : ""}`}
                >
                    {items.map((g, i) => (
                        <StaggerItem
                            key={`${g.src}-${i}`}
                            className={`${g.span} ${stagger && i % 2 === 1 ? "lg:mt-12" : ""}`}
                        >
                            <figure
                                data-testid={`gallery-item-${i}`}
                                onClick={() => setActive(i)}
                                className="group relative cursor-zoom-in overflow-hidden rounded-3xl bg-white"
                            >
                                <div className={`${g.ratio} overflow-hidden`}>
                                    <img
                                        src={g.src}
                                        alt={g.caption}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.215,0.61,0.355,1)] group-hover:scale-[1.06]"
                                    />
                                </div>
                                <div className="pointer-events-none absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/10" />
                                <figcaption className="absolute bottom-4 left-4 rounded-full bg-white/85 px-4 py-2 text-[0.68rem] font-light uppercase tracking-[0.18em] text-body backdrop-blur-md">
                                    {g.caption}
                                </figcaption>
                            </figure>
                        </StaggerItem>
                    ))}
                </Stagger>
            </div>

            {isOpen && (
                <div
                    data-testid="gallery-lightbox"
                    onClick={close}
                    className="fixed inset-0 z-[120] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md sm:p-8 animate-[fadeIn_0.3s_ease]"
                >
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Chiudi"
                        data-testid="lightbox-close"
                        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white/80 transition-all duration-300 hover:border-white hover:bg-white/10 hover:text-white sm:right-8 sm:top-8"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            go(-1);
                        }}
                        aria-label="Foto precedente"
                        data-testid="lightbox-prev"
                        className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white/80 transition-all duration-300 hover:border-white hover:bg-white/10 hover:text-white sm:left-8"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            go(1);
                        }}
                        aria-label="Foto successiva"
                        data-testid="lightbox-next"
                        className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white/80 transition-all duration-300 hover:border-white hover:bg-white/10 hover:text-white sm:right-8"
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>

                    <figure
                        onClick={(e) => e.stopPropagation()}
                        className="flex max-h-full w-full max-w-5xl flex-col items-center"
                    >
                        <img
                            src={items[active].src}
                            alt={items[active].caption}
                            data-testid="lightbox-image"
                            className="max-h-[80vh] w-auto rounded-2xl object-contain shadow-2xl"
                        />
                        <figcaption className="mt-5 text-xs font-light uppercase tracking-[0.25em] text-white/70">
                            {items[active].caption}
                            <span className="ml-3 text-white/40">
                                {active + 1} / {items.length}
                            </span>
                        </figcaption>
                    </figure>
                </div>
            )}
        </section>
    );
}
